// web/src/pages/admin/PaymentTypesPage.tsx
//
// I-12 — Admin CRUD for payment-type codes (`/api/admin/payment-types`).
//
// Foundation rows (Cash, Cheque, EFT, CC, BPay) are displayed with a lock
// indicator and have their delete button disabled. NB the SPA mirrors the
// backend constant here — no API surface exposes "is foundation". If you add
// or remove members from the backend's foundation set, mirror the edit in
// `FOUNDATION_PAYMENT_TYPES` below.
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Lock } from "lucide-react";
import { z } from "zod";
import {
  listPaymentTypes,
  createPaymentType,
  updatePaymentType,
  deletePaymentType,
  type PaymentTypeDto,
} from "@/lib/invoices-payments";
import { ListPage } from "@/archetypes/ListPage";
import { DataGrid, type DataGridColumn } from "@/components/data-grid";
import { IntentButton } from "@/components/intent-button";
import { DetailsDrawer } from "@/components/details-drawer";
import { EntityForm } from "@/components/entity-form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

// Mirror of foundation payment types in the backend.
// Keep in sync with the backend's PaymentTypeCodes foundation set.
export const FOUNDATION_PAYMENT_TYPES: ReadonlySet<string> = new Set([
  "Cash",
  "Cheque",
  "EFT",
  "CC",
  "BPay",
]);

const schema = z.object({
  paymentTypeCode: z.string().min(1).max(20),
  description: z.string().min(1).max(100),
  active: z.boolean(),
});
type Form = z.infer<typeof schema>;

export function PaymentTypesPage() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<PaymentTypeDto | null>(null);
  const [creating, setCreating] = useState(false);

  const list = useQuery({
    queryKey: ["payment-types"],
    queryFn: () => listPaymentTypes(),
  });

  const upsert = useMutation({
    mutationFn: (form: Form) =>
      editing
        ? updatePaymentType(editing.paymentTypeCode, {
            description: form.description,
            active: form.active,
          })
        : createPaymentType({
            paymentTypeCode: form.paymentTypeCode,
            description: form.description,
            active: form.active,
          }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["payment-types"] });
      setEditing(null);
      setCreating(false);
    },
  });

  const del = useMutation({
    mutationFn: (code: string) => deletePaymentType(code),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["payment-types"] }),
  });

  const cols: DataGridColumn<PaymentTypeDto>[] = [
    {
      id: "code",
      header: "Code",
      accessor: (r) => (
        <span className="inline-flex items-center gap-1">
          {FOUNDATION_PAYMENT_TYPES.has(r.paymentTypeCode) && (
            <Lock aria-label="Foundation row (delete locked)" className="size-3 text-muted-foreground" />
          )}
          {r.paymentTypeCode}
        </span>
      ),
    },
    { id: "desc",   header: "Description", accessor: (r) => r.description },
    { id: "active", header: "Active",       accessor: (r) => r.active ? "Yes" : "—" },
    {
      id: "actions",
      header: "",
      accessor: (r) => {
        const isFoundation = FOUNDATION_PAYMENT_TYPES.has(r.paymentTypeCode);
        return (
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              aria-label={`Edit ${r.paymentTypeCode}`}
              onClick={() => setEditing(r)}
            >
              Edit
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              aria-label={`Delete ${r.paymentTypeCode}`}
              disabled={isFoundation || del.isPending}
              onClick={() => {
                if (!isFoundation) del.mutate(r.paymentTypeCode);
              }}
            >
              Delete
            </Button>
          </div>
        );
      },
      align: "right",
    },
  ];

  const drawerOpen = creating || editing !== null;
  const defaultValues: Form = editing
    ? {
        paymentTypeCode: editing.paymentTypeCode,
        description: editing.description,
        active: editing.active,
      }
    : { paymentTypeCode: "", description: "", active: true };

  return (
    <ListPage
      title="Payment types"
      primaryAction={
        <IntentButton onClick={() => setCreating(true)}>+ New</IntentButton>
      }
    >
      <DataGrid
        rows={list.data ?? []}
        columns={cols}
        rowKey={(r) => r.paymentTypeCode}
        totalRows={list.data?.length ?? 0}
        pageIndex={0}
        pageSize={Math.max(50, list.data?.length ?? 0)}
        loading={list.isLoading}
        onPageChange={() => undefined}
      />
      <DetailsDrawer
        open={drawerOpen}
        onOpenChange={(o) => {
          if (!o) {
            setEditing(null);
            setCreating(false);
          }
        }}
        title={editing ? "Edit payment type" : "New payment type"}
      >
        <EntityForm<Form>
          schema={schema}
          defaultValues={defaultValues}
          onSubmit={(v) => upsert.mutate(v)}
        >
          {(form) => (
            <div className="space-y-3">
              <div>
                <Label htmlFor="pt-code">Code</Label>
                <Input id="pt-code" disabled={editing !== null} {...form.register("paymentTypeCode")} />
              </div>
              <div>
                <Label htmlFor="pt-desc">Description</Label>
                <Input id="pt-desc" {...form.register("description")} />
              </div>
              <label className="flex items-center gap-2">
                <input type="checkbox" {...form.register("active")} /> Active
              </label>
              <Button type="submit">Save</Button>
            </div>
          )}
        </EntityForm>
      </DetailsDrawer>
    </ListPage>
  );
}
